import type { MaterialSymbolName } from '@shared/types/materialSymbolNames';
import type { Organisation } from '@@/packages/shared/types/models/organisation';
import { hasPermissionCategory } from '@/modules/current-user/permissions/hasPermissionCategory';
import type { Mode } from './types';

export interface AvailableMode {
  mode: Exclude<Mode, ''>;
  // The route a user is sent to when selecting this mode. Navigating there
  // syncs `currentMode` from `to.meta.mode` once the navigation is confirmed.
  route: string;
  icon: MaterialSymbolName;
  // Whether the current user is permitted to enter this mode. Modes the org
  // has but the user cannot access are shown disabled rather than hidden.
  hasPermission: boolean;
}

export interface ModePermissions {
  client: boolean;
  supplier: boolean;
  federated: boolean;
}

// The icon shown for each mode, both in the switcher's mode list and its
// conversion CTAs. Single source of truth so the two can never drift apart.
export const MODE_ICONS: Record<Exclude<Mode, ''>, MaterialSymbolName> = {
  federated: 'lan',
  client: 'manage_search',
  supplier: 'badge',
};

// Map a raw permission list to the per-mode permission flags. Kept next to
// ModePermissions so the category-to-mode mapping lives in one place.
export function toModePermissions(permissions: string[]): ModePermissions {
  return {
    client: hasPermissionCategory(permissions, 'clients'),
    supplier: hasPermissionCategory(permissions, 'suppliers'),
    federated: hasPermissionCategory(permissions, 'federated'),
  };
}

// A client-restricted org has no client side at all, so `isClient` on its own
// never means the client routes are reachable.
function hasClientSide(org: Organisation): boolean {
  return org.isClient && !org.isClientRestricted;
}

// The single source of truth for which modes an org supports, in display and
// priority order (federated, client, supplier), and whether the current user
// may enter each. Both the switcher (useAvailableModes) and the default-mode
// resolver (setModeFromOrg) derive from this so the modes offered and the mode
// resolved can never drift apart. Federated is only offered when the org is
// federated, and being federated never removes the client/supplier options.
export function getAvailableModes(
  org: Organisation,
  permissions: ModePermissions,
): AvailableMode[] {
  const modes: AvailableMode[] = [];
  if (org.isFederated) {
    modes.push({
      mode: 'federated',
      route: '/f/',
      icon: MODE_ICONS.federated,
      hasPermission: permissions.federated,
    });
  }
  if (hasClientSide(org)) {
    modes.push({
      mode: 'client',
      route: '/c/',
      icon: MODE_ICONS.client,
      hasPermission: permissions.client,
    });
  }
  if (org.isSupplier) {
    modes.push({
      mode: 'supplier',
      route: '/s/',
      icon: MODE_ICONS.supplier,
      hasPermission: permissions.supplier,
    });
  }
  return modes;
}

// Resolve which mode an org should land on, given the user's permissions and a
// stored "last-used" preference. A stored preference wins only when the org has
// that mode and the user may enter it; failing that we land on the first mode
// the user can actually use (federated, then client, then supplier), and only
// if none are permitted do we fall back to the org's primary type. Pure, so it
// can be driven by permissions read fresh from the query cache rather than a
// reactive observer that may still hold a previous org's permissions.
export function resolveModeForOrg(
  org: Organisation,
  permissions: ModePermissions,
  storedMode: string | null,
): Mode {
  const modes = getAvailableModes(org, permissions);

  const preferred = modes.find((m) => m.mode === storedMode && m.hasPermission);
  if (preferred) return preferred.mode;

  const permitted = modes.find((m) => m.hasPermission);
  if (permitted) return permitted.mode;

  const [primary] = modes;
  return primary?.mode ?? '';
}
